import { inject, Injectable } from '@angular/core';
import {
    BehaviorSubject,
    catchError,
    combineLatest,
    debounceTime,
    delay,
    distinctUntilChanged,
    filter,
    first,
    map,
    Observable,
    of,
    shareReplay,
    switchMap,
    take,
    tap,
} from 'rxjs';
import { ReplenishmentTaskOrchestrator } from '../services/replenishment-task-orchestrator.service';
import { CpwTaskStateService } from '../infrastructure/cpw-task-state.service';
import {
    EquipmentDto,
    ReplenishmentDataService,
    ReplenishmentDto,
    ReplenishmentFacade,
    WarehouseFacade,
} from '@pf/platform-wms/domain';
import { HandlingUnitAssignmentService } from '../services/handling-unit-assignment.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LoggerService, UserPermissionCheckService } from '@pf/shared-services';
import { RoutesForReplenishments } from './replenishments.routes';
import { ReplenishmentTask } from '../models/replenishment-task.model';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { asArraySafe } from '@pf/shared-utility';
import { UserPositionFacade } from '@pf/platform-core/domain';
import { ReplenishmentContext } from '../models/replenishment-context.model';
import { isHandlingLtl } from '../models/cpw-equipment-types';
import { ContainerStatus } from '@control-tower/platform-wms';
import { PickingTask } from '../models/picking-task.model';

@UntilDestroy()
@Injectable()
export class ReplenishmentsFacade {
    private readonly userPositionFacade = inject(UserPositionFacade);
    private readonly warehouseFacade = inject(WarehouseFacade);
    private readonly replenishmentFacade = inject(ReplenishmentFacade);
    private readonly handlingUnitService = inject(
        HandlingUnitAssignmentService
    );

    private taskStateService = inject(CpwTaskStateService);
    private routes = inject(RoutesForReplenishments);
    private replenishmentDataService = inject(ReplenishmentDataService);
    private readonly taskOrchestrator = inject(ReplenishmentTaskOrchestrator);
    private userPermissionCheckService = inject(UserPermissionCheckService);
    private logger = inject(LoggerService);
    private tasksLoadingState = new BehaviorSubject<
        'not-loaded' | 'loading' | 'loaded'
    >('not-loaded');

    private filteredTasks$ = combineLatest([
        this.taskStateService.tasks$,
        this.taskStateService.taskFilters$,
    ]).pipe(
        map(([tasks, filters]) => {
            return (tasks as ReplenishmentTask[]).filter(task => {
                const zoneFilter =
                    filters.zones.length === 0 ||
                    task.replenishmentSummaries
                        .map(summary => [summary.fromZone, summary.toZone])
                        .flat()
                        .some(zone => filters.zones.includes(zone.id));
                const equipmentTypesFilter =
                    filters.equipmentTypes.length === 0 ||
                    task.replenishmentSummaries
                        .map(summary => summary.equipmentType)
                        .some(
                            equipmentType =>
                                equipmentType &&
                                filters.equipmentTypes.includes(
                                    equipmentType.id as string
                                )
                        );
                return zoneFilter && equipmentTypesFilter;
            });
        }),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    pendingReplenishments$ = this.filteredTasks$.pipe(
        map(tasks => tasks.filter(task => task.section === 'pending')),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    activeReplenishments$ = this.filteredTasks$.pipe(
        map(tasks => tasks.filter(task => task.section === 'replenishing')),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    activeReplenishmentTask$: Observable<ReplenishmentTask> =
        this.taskStateService.activeTask$.pipe(
            map(task => task as ReplenishmentTask),
            shareReplay({ bufferSize: 1, refCount: true })
        );

    loadReplenishments() {
        this.tasksLoadingState.next('loading');
        this.taskStateService.clearTasks();

        this.replenishmentFacade.clearPages();

        return combineLatest([
            this.replenishmentFacade.pendingReplenishments$(),
            this.replenishmentFacade.activeReplenishments$(),
            this.getUserSessionReplenishments$(),
            this.userPermissionCheckService.checkPermission$(
                'WMS:ForkliftOperator'
            ),
        ]).pipe(
            untilDestroyed(this),
            debounceTime(300),
            tap(
                ([
                    pendingReplenishments,
                    activeReplenishments,
                    _,
                    showLtlReplenishments,
                ]) => {
                    this.taskOrchestrator.orchestrate(
                        this.warehouseFacade.active!,
                        pendingReplenishments.filter(r => !r.isDeleted),
                        activeReplenishments.filter(r => !r.isDeleted),
                        showLtlReplenishments
                    );
                }
            ),
            tap(() => {
                this.tasksLoadingState.next('loaded');
            }),
            map(() => true)
        );
    }

    getUserSessionReplenishments$() {
        return this.userPositionFacade.active$.pipe(
            filter(Boolean),
            map(userPosition =>
                asArraySafe(
                    (userPosition!.context as ReplenishmentContext)
                        .replenishmentIds
                )
            ),
            distinctUntilChanged(
                (a, b) => JSON.stringify(a) === JSON.stringify(b)
            ),
            switchMap(replenishmentIds => {
                if (!replenishmentIds.length) {
                    return of([]);
                }

                return this.replenishmentFacade.getMany$(replenishmentIds);
            })
        );
    }

    loadActiveReplenishmentTask$() {
        if (this.taskStateService.getActiveTask()) {
            return of(true);
        }

        return this.tasksLoadingState.pipe(
            filter(state => state !== 'loading'),
            take(1),
            switchMap(state => {
                if (state === 'not-loaded') {
                    return this.loadReplenishments().pipe(delay(100));
                }

                return of(true);
            }),
            switchMap(() => this.activeReplenishments$),
            tap(tasks => {
                if (tasks.length === 0) {
                    this.clearActiveReplenishmentTask();
                } else {
                    this.taskStateService.setActiveTask(tasks[0].id);
                }
            }),
            map(() => true)
        );
    }

    navigateToQueue() {
        this.routes.queue().navigate();
    }

    setActiveReplenishmentId(replenishment: ReplenishmentDto) {
        this.taskStateService
            .performWithActiveTask$((task: ReplenishmentTask) => {
                if (task.context.activeReplenishmentId !== replenishment.id) {
                    return this.taskStateService
                        .updateTaskContext$(task.id, {
                            activeReplenishmentId: replenishment.id,
                            scannedSecondarySlot: null,
                            scannedSecondaryItem: null,
                            scannedPrimarySlot: null,
                            scannedPrimaryItem: null,
                            scannedQuantity: null,
                        })
                        .pipe(map(() => true));
                }
                return of(true);
            }, of(false))
            .subscribe();
    }

    setActiveReplenishmentSecondaryScanValues(
        slotBarcode: string,
        itemBarcode: string
    ) {
        this.taskStateService
            .performWithActiveTask$((task: ReplenishmentTask) => {
                return this.taskStateService
                    .updateTaskContext$(task.id, {
                        scannedSecondarySlot: slotBarcode,
                        scannedSecondaryItem: itemBarcode,
                    })
                    .pipe(map(() => true));
            }, of(false))
            .subscribe();
    }

    setActiveReplenishmentQuantity(quantity: number) {
        this.taskStateService
            .performWithActiveTask$((task: ReplenishmentTask) => {
                return this.taskStateService
                    .updateTaskContext$(task.id, {
                        scannedQuantity: quantity,
                    })
                    .pipe(map(() => true));
            }, of(false))
            .subscribe();
    }

    setActiveReplenishmentPrimaryScanValues(
        slotBarcode: string,
        itemBarcode: string
    ) {
        this.taskStateService
            .performWithActiveTask$((task: ReplenishmentTask) => {
                return this.taskStateService
                    .updateTaskContext$(task.id, {
                        scannedPrimarySlot: slotBarcode,
                        scannedPrimaryItem: itemBarcode,
                    })
                    .pipe(map(() => true));
            }, of(false))
            .subscribe();
    }

    clearActiveReplenishmentScanValues$() {
        return this.taskStateService.performWithActiveTask$(
            (task: ReplenishmentTask) => {
                return this.taskStateService
                    .updateTaskContext$(task.id, {
                        scannedSecondarySlot: null,
                        scannedSecondaryItem: null,
                        scannedPrimarySlot: null,
                        scannedPrimaryItem: null,
                        scannedQuantity: null,
                    })
                    .pipe(map(() => true));
            },
            of(false)
        );
    }

    setActiveReplenishmentTask(taskId?: string): void {
        if (!taskId) {
            this.clearActiveReplenishmentTask();
            return;
        }

        this.taskStateService.setActiveTask(taskId);
        this.routes.replenish().navigate();
    }

    claimActiveReplenishmentTask$(): Observable<boolean> {
        return this.taskStateService.performWithActiveTask$(
            (activeTask: ReplenishmentTask) => {
                if (!activeTask.replenishmentSummaries.length) {
                    return of(false);
                }

                const replenishmentIds = activeTask.replenishmentSummaries.map(
                    summary => summary.replenishmentId
                );

                return this.replenishmentDataService
                    .claimReplenishments$(
                        activeTask.warehouseId,
                        replenishmentIds
                    )
                    .pipe(
                        tap(() => {
                            this.taskStateService.claimActiveTask();
                        }),
                        switchMap(() => {
                            return this.taskStateService.updateTaskContext$<ReplenishmentContext>(
                                activeTask.id,
                                {
                                    replenishmentIds,
                                }
                            );
                        }),
                        map(() => true),
                        catchError(() => of(false))
                    );
            },
            of(false)
        );
    }

    claimNextReplenishmentTask$() {
        return combineLatest([
            this.assignedHandlingUnits$,
            this.pendingReplenishments$,
        ]).pipe(
            first(),
            map(([assignedHandlingUnits, pendingReplenishments]) => {
                if (!pendingReplenishments?.length) {
                    return null;
                }
                if (this.isHandlingLtl(assignedHandlingUnits)) {
                    return (
                        pendingReplenishments.find(pick => pick.isLtl) ?? null
                    );
                }
                return pendingReplenishments.find(pick => !pick.isLtl) ?? null;
            }),
            switchMap((replenishmentTask: ReplenishmentTask | null) => {
                if (replenishmentTask) {
                    return this.claimActiveReplenishmentTask$().pipe(
                        tap(() =>
                            this.setActiveReplenishmentTask(
                                replenishmentTask.id
                            )
                        ),
                        map(() => true)
                    );
                }
                return of(false);
            })
        );
    }

    completeTask$() {
        return this.resetContext$().pipe(
            tap(() => {
                this.taskStateService.clearActiveTask();
                this.navigateToQueue();
            })
        );
    }

    releaseActiveReplenishmentTask$(): Observable<boolean> {
        return this.taskStateService.performWithActiveTask$(
            (activeTask: ReplenishmentTask) => {
                const replenishmentIds = activeTask.replenishmentSummaries.map(
                    summary => summary.replenishmentId
                );
                return this.replenishmentDataService
                    .unClaimReplenishments$(
                        activeTask.warehouseId,
                        replenishmentIds
                    )
                    .pipe(
                        switchMap(() => this.resetContext$()),
                        tap(() => {
                            this.clearActiveReplenishmentTask();
                        }),
                        map(() => true),
                        catchError(() => of(false))
                    );
            },
            of(false)
        );
    }

    clearActiveReplenishmentTask() {
        this.taskStateService.clearActiveTask();
        this.navigateToQueue();
    }

    assignEquipmentToReplenishment$(
        equipment: EquipmentDto
    ): Observable<boolean> {
        return this.handlingUnitService.addHandlingUnit$(equipment);
    }

    assignedHandlingUnits$: Observable<EquipmentDto[]> =
        this.handlingUnitService.assignedHandlingUnits$;

    hasEquipmentForReplenishment$: Observable<boolean> = combineLatest([
        this.assignedHandlingUnits$,
        this.activeReplenishments$,
    ]).pipe(
        map(([assignedHandlingUnits, activeReplenishments]) => {
            if (assignedHandlingUnits.length === 0) {
                return false;
            }

            if (activeReplenishments.some(x => x.isLtl)) {
                return assignedHandlingUnits.some(equipment =>
                    isHandlingLtl(equipment.equipmentType)
                );
            }

            if (activeReplenishments.some(x => !x.isLtl)) {
                return assignedHandlingUnits.some(
                    equipment => !isHandlingLtl(equipment.equipmentType)
                );
            }

            return false;
        })
    );

    private resetContext$() {
        return this.taskStateService.performWithActiveTask$(
            (task: ReplenishmentTask) => {
                return this.taskStateService
                    .updateTaskContext$(task.id, {
                        replenishmentIds: [],
                        latestHandlingUnitId: '',
                        activeReplenishmentId: '',
                        scannedPrimarySlot: null,
                        scannedPrimaryItem: null,
                        scannedSecondarySlot: null,
                        scannedSecondaryItem: null,
                        scannedQuantity: null,
                    })
                    .pipe(map(() => true));
            },
            of(false)
        );
    }

    private isHandlingLtl(assignedHandlingUnits: EquipmentDto[]) {
        if (!assignedHandlingUnits?.length) {
            return false;
        }
        return assignedHandlingUnits.some(hu =>
            isHandlingLtl(hu.equipmentType)
        );
    }

    canClaimMoreReplenishments$ = combineLatest([
        this.assignedHandlingUnits$,
        this.pendingReplenishments$,
    ]).pipe(
        map(([assignedHandlingUnits, pendingReplenishments]) => {
            if (!pendingReplenishments?.length) {
                return false;
            }
            if (this.isHandlingLtl(assignedHandlingUnits)) {
                return pendingReplenishments.some(
                    replenishment => replenishment.isLtl
                );
            }

            return (
                assignedHandlingUnits
                    .map(equipment => asArraySafe(equipment.containers))
                    .flat()
                    .filter(
                        container =>
                            container.status === ContainerStatus.Unassigned
                    ).length > 0
            );
        })
    );
}
